// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Qui est connecté (chantier ⑯, 30/09/2026).
//
// ⛔ Ce contexte n'a PLUS DE JETON : la session vit dans un cookie `HttpOnly`
// que la page ne peut pas lire (cf lib/session.js). « Suis-je connecté ? » se
// demande donc au SERVEUR (`/api/auth/me`) au démarrage ; tant qu'il n'a pas
// répondu, `loading` est vrai et `ProtectedRoute` n'affiche aucune page.
//
// ⛔ Le profil n'est plus recopié dans `localStorage` : il vit en mémoire, et
// `/api/auth/me` le redonne à chaque chargement de page.
import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { nettoyerHeritageLocal, oublierDonneesDuCompte, revendiquerDonnees } from '../lib/stockageSession';
import { EVENEMENT_SESSION_PERDUE } from '../lib/session';
import { disconnectSocket } from '../services/socket';

const AuthContext = createContext(null);
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Les onglets d'un même navigateur partagent les cookies, donc la session.
const CANAL_ONGLETS = 'normascan_session';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Pour les écouteurs posés une seule fois (onglets, cache, session perdue),
  // qui ne verraient sinon que le `user` du premier rendu.
  const userIdRef = useRef(null);
  const canalRef = useRef(null);

  const appliquerUser = (u) => {
    userIdRef.current = u?.id ?? null;
    setUser(u);
  };

  const diffuser = (type) => {
    try { canalRef.current?.postMessage({ type, userId: userIdRef.current }); } catch (_) { /* onglet seul */ }
  };

  // Relit le compte connecté. Rend le profil, `null` si personne n'est
  // connecté, `undefined` si le serveur n'a pas pu répondre (on ne sait pas).
  const refreshUser = async () => {
    try {
      // Le 401 d'un jeton expiré a DÉJÀ été rattrapé par un renouvellement
      // (lib/session.js) : un 401 ici veut dire que la session est finie.
      const response = await fetch(`${API_BASE_URL}/api/auth/me`);
      if (response.status === 401) {
        if (userIdRef.current) setSessionExpired(true);
        appliquerUser(null);
        return null;
      }
      if (!response.ok) {
        console.error('❌ Erreur refresh user:', response.status);
        return undefined;
      }
      const userData = await response.json();
      // AVANT de publier le compte : les pages lisent `sessionStorage` dès leur
      // premier rendu (cf lib/stockageSession.js).
      revendiquerDonnees(userData.id);
      appliquerUser(userData);
      setSessionExpired(false);
      return userData;
    } catch (error) {
      console.error('❌ Erreur refresh user:', error);
      return undefined;
    }
  };

  // Démarrage : le serveur dit qui est connecté.
  useEffect(() => {
    // Héritage d'avant le 30/09/2026 : jeton, profil, conversations de NORMA
    // vivaient dans `localStorage`, sur le disque (chantiers ⑮ et ⑯). Le jeton
    // n'a plus cours (le serveur refuse tout jeton sans session), et le reste
    // n'a rien à faire là.
    nettoyerHeritageLocal();

    refreshUser().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ⚠️ DEUX ONGLETS PARTAGENT LA MÊME SESSION (les cookies). Se connecter avec
  // un autre compte dans un onglet laissait le premier AFFICHER l'ancien
  // compte : écrans, données déjà chargées, entrées de menu (constaté au test
  // du 22/09/2026). L'événement `storage` le signalait tant que le jeton était
  // dans `localStorage` ; c'est désormais un `BroadcastChannel`.
  //
  // Le BOUTON « PAGE PRÉCÉDENTE » fait pire : le navigateur restitue la page
  // entière depuis son cache aller-retour (`bfcache`), sans rejouer le moindre
  // appel ni le moindre rendu React. On redemande donc le compte au retour, et
  // l'on recharge s'il a changé.
  useEffect(() => {
    try { canalRef.current = new BroadcastChannel(CANAL_ONGLETS); } catch (_) { canalRef.current = null; }

    const surMessage = (e) => {
      const { type, userId } = e.data || {};
      // Rechargement complet, et non un simple `refreshUser` : les pages déjà
      // rendues portent les données de l'ancien compte, y compris dans des
      // composants que ce contexte ne connaît pas.
      if (type === 'connexion' && userId !== userIdRef.current) window.location.reload();
      if (type === 'deconnexion' && userIdRef.current) window.location.reload();
    };

    const surRetourDeCache = (e) => {
      if (!e.persisted) return;
      const avant = userIdRef.current;
      refreshUser().then((u) => {
        if (u !== undefined && (u?.id ?? null) !== avant) window.location.reload();
      });
    };

    // Le renouvellement a échoué (plafond de 12 h, déconnexion depuis un autre
    // appareil, vol détecté) : `ProtectedRoute` renvoie alors vers /login.
    const surSessionPerdue = () => {
      if (userIdRef.current) {
        setSessionExpired(true);
        appliquerUser(null);
      }
    };

    canalRef.current?.addEventListener('message', surMessage);
    window.addEventListener('pageshow', surRetourDeCache);
    window.addEventListener(EVENEMENT_SESSION_PERDUE, surSessionPerdue);
    return () => {
      canalRef.current?.removeEventListener('message', surMessage);
      canalRef.current?.close();
      window.removeEventListener('pageshow', surRetourDeCache);
      window.removeEventListener(EVENEMENT_SESSION_PERDUE, surSessionPerdue);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // La session est DÉJÀ ouverte : le serveur a posé ses cookies en répondant
  // au login (ou au MFA, à l'invitation, à l'inscription). On publie le compte.
  const login = (userData) => {
    // D'abord : les données locales d'un AUTRE compte sont effacées.
    revendiquerDonnees(userData?.id);
    appliquerUser(userData);
    setSessionExpired(false);
    diffuser('connexion');

    // ✅ Rafraîchir immédiatement pour avoir les quotas
    setTimeout(() => refreshUser(), 100);
  };

  // Ferme la session de CET appareil côté serveur (décision de Marc du
  // 30/09/2026), puis tout ce qui en restait dans la page. À ATTENDRE avant
  // une navigation complète (`window.location.href = …`), qui interromprait
  // la requête.
  const logout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/logout`, { method: 'POST' });
    } catch (_) { /* hors ligne : le cookie expirera de lui-même */ }
    // Historique de NORMA, analyse en cours, fichier déposé, projet —
    // tout ce qui est au compte (seuls thème et langue de l'appareil restent).
    oublierDonneesDuCompte();
    disconnectSocket();
    diffuser('deconnexion');
    appliquerUser(null);
  };

  const isAuthenticated = () => !!user;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAuthenticated, refreshUser, sessionExpired, setSessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans AuthProvider');
  }
  return context;
};
